import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // ✅ allowed layouts (add more if needed)
  static allowedLayouts = {
    desktop: ['default', 'cols-3', 'cols-5', 'cols-6', 'zoom-out'],
    mobile: ['default', 'mobile-single'],
  };

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    // ✅ Restore saved layout on load for the current viewport
    this.#restoreStoredLayout();

    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const allowed = ResultsList.allowedLayouts[viewport] || [];

    // ✅ Prevent unsupported values from doing anything
    if (!allowed.includes(target.value)) return;

    this.#animateLayoutChange(target.value, viewport);
  }

  /**
   * Sets the layout with animation + stores it.
   *
   * @param {string} value
   * @param {'desktop'|'mobile'} viewport
   */
  #animateLayoutChange = async (value, viewport) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Applies layout attribute to grid.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Restore saved layout for current viewport and sync the radio UI.
   */
  #restoreStoredLayout() {
    const { grid } = this.refs;
    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const allowed = ResultsList.allowedLayouts[viewport] || [];

    const stored = sessionStorage.getItem(`product-grid-view-${viewport}`);
    const valueToApply = stored && allowed.includes(stored) ? stored : 'default';

    this.#setLayout(valueToApply);
    this.#syncRadioState(valueToApply, viewport);
  }

  /**
   * Sync the correct radio input as checked (desktop uses name="grid", mobile uses name="grid-mobile")
   */
  #syncRadioState(value, viewport) {
    const name = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const input = this.querySelector(`input[type="radio"][name="${name}"][value="${value}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  /**
   * Handles viewport switch (mobile <-> desktop)
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = () => {
    // ✅ When switching viewport, restore that viewport's saved layout
    this.#restoreStoredLayout();
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
